import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  TrendingUp, TrendingDown, RefreshCw, AlertCircle, Clock, Zap, Loader2,
  ShieldAlert, CheckCircle2, Wifi, WifiOff,
} from "lucide-react";

const fmt = (n, d = 2) =>
  n == null || isNaN(n) ? "—" : Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: d, maximumFractionDigits: d,
  });

const STATUS_META = {
  watching:  { label: "WATCHING",   cls: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  triggered: { label: "TRIGGERED",  cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  expired:   { label: "EXPIRED",    cls: "bg-zinc-700/40 text-zinc-500 border-zinc-700/30" },
};

function CandidateRow({ c, onChain }) {
  const meta = STATUS_META[c.status] || STATUS_META.watching;
  const isBull = c.direction === "BULLISH";
  const broke  = c.distance_pct > 0;

  return (
    <div className={`panel p-4 space-y-3 border ${broke ? "border-emerald-500/30" : "border-[var(--border)]"}`}>
      {/* Header row */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`p-1.5 rounded ${isBull ? "bg-emerald-500/10" : "bg-red-500/10"}`}>
            {isBull
              ? <TrendingUp className="w-4 h-4 text-emerald-400" />
              : <TrendingDown className="w-4 h-4 text-red-400" />}
          </div>
          <div>
            <div className="font-mono-data text-white font-semibold">{c.symbol}</div>
            <div className={`text-xs font-section ${isBull ? "text-emerald-400" : "text-red-400"}`}>
              {isBull ? "Open = Low · Buy CE" : "Open = High · Buy PE"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-section px-2 py-0.5 border ${meta.cls}`}>
            {meta.label}
          </span>
          <span className={`text-xs font-mono-data ${c.pct_change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
            {c.pct_change >= 0 ? "+" : ""}{fmt(c.pct_change)}%
          </span>
        </div>
      </div>

      {/* Opening range grid */}
      <div className="grid grid-cols-4 gap-3 text-center">
        {[
          { label: "OR Low",   val: c.or_low,   hi: false },
          { label: "OR Open",  val: c.or_open,  hi: false },
          { label: "OR High",  val: c.or_high,  hi: false },
          { label: "LTP",      val: c.ltp,      hi: broke },
        ].map(({ label, val, hi }) => (
          <div key={label} className="bg-[var(--bg-page)] p-2">
            <div className="text-[10px] txt-muted overline mb-0.5">{label}</div>
            <div className={`font-mono-data text-sm ${hi ? "text-emerald-400 font-bold" : "text-white"}`}>
              ₹{fmt(val)}
            </div>
          </div>
        ))}
      </div>

      {/* Breakout distance */}
      <div className="flex items-center justify-between text-xs">
        <span className="txt-muted">
          {isBull
            ? `SL: ₹${fmt(c.or_low)} · Target: ₹${fmt(c.or_high + 2 * (c.or_high - c.or_low))}`
            : `SL: ₹${fmt(c.or_high)} · Target: ₹${fmt(c.or_low - 2 * (c.or_high - c.or_low))}`}
        </span>
        <span className={`font-mono-data ${broke ? "text-emerald-400" : "txt-muted"}`}>
          {broke
            ? `+${fmt(c.distance_pct)}% above OR`
            : `${fmt(Math.abs(c.distance_pct))}% to breakout`}
        </span>
      </div>

      {/* Options chain button */}
      <button
        onClick={() => onChain(c.symbol)}
        className="w-full text-xs font-section tracking-wider border border-[var(--border)] py-1.5 txt-muted hover:text-white hover:border-white/30 transition-colors"
      >
        VIEW OPTIONS CHAIN
      </button>
    </div>
  );
}

function ChainModal({ symbol, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/orb/chain/${symbol}`)
      .then(r => setData(r.data))
      .catch(() => toast.error("Failed to load chain"))
      .finally(() => setLoading(false));
  }, [symbol]);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] w-full max-w-2xl max-h-[80vh] overflow-auto"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
          <div className="font-display text-lg">{symbol} Options Chain</div>
          <div className="flex items-center gap-3">
            {data && <span className="txt-muted text-sm">LTP: <span className="text-white font-mono-data">₹{fmt(data.ltp)}</span></span>}
            <button onClick={onClose} className="txt-muted hover:text-white text-lg leading-none">×</button>
          </div>
        </div>

        {loading && (
          <div className="p-8 text-center txt-muted text-sm">Loading chain…</div>
        )}

        {!loading && data && (
          <div className="p-4">
            <div className="grid grid-cols-7 gap-1 text-[10px] txt-muted overline mb-2 text-center">
              <span>CE OI</span><span>CE Vol</span><span className="text-emerald-400">CE LTP</span>
              <span className="text-white">STRIKE</span>
              <span className="text-red-400">PE LTP</span><span>PE Vol</span><span>PE OI</span>
            </div>
            {(data.chain?.rows || []).map(row => (
              <div key={row.strike}
                   className="grid grid-cols-7 gap-1 text-xs text-center py-1.5 border-b border-[var(--border)]/40 hover:bg-[var(--bg-surface-2)]">
                <span className="txt-muted">{fmt(row.ce?.oi ?? 0, 0)}</span>
                <span className="txt-muted">{fmt(row.ce?.volume ?? 0, 0)}</span>
                <span className="text-emerald-400 font-mono-data">₹{fmt(row.ce?.price)}</span>
                <span className="text-white font-mono-data font-semibold">{row.strike}</span>
                <span className="text-red-400 font-mono-data">₹{fmt(row.pe?.price)}</span>
                <span className="txt-muted">{fmt(row.pe?.volume ?? 0, 0)}</span>
                <span className="txt-muted">{fmt(row.pe?.oi ?? 0, 0)}</span>
              </div>
            ))}
            {!(data.chain?.rows?.length) && (
              <div className="p-6 text-center txt-muted text-sm">No chain data available</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DiagnosticPanel({ status, onRefreshToken, refreshing }) {
  if (!status) return null;
  const { dhan_connection: dc, mcx_missing_ids, today_candidates, signals_last_14d, diagnosis } = status;
  const hasIssues = diagnosis.some(d => !d.startsWith("No issues"));
  const dhanOk = dc.connected && dc.api_ok;

  return (
    <div className={`panel p-4 space-y-3 border ${hasIssues ? "border-red-500/40" : "border-emerald-500/30"}`}>
      <div className="flex items-center gap-2">
        <ShieldAlert className={`w-4 h-4 ${hasIssues ? "text-red-400" : "text-emerald-400"}`} />
        <span className="font-section text-sm tracking-wider text-white">SCANNER DIAGNOSTIC</span>
        <span className="text-xs txt-muted ml-auto">Admin view</span>
      </div>

      {/* Dhan connection — record + live API probe */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        {dhanOk
          ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
          : <WifiOff className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />}
        <span className={dhanOk ? "text-emerald-400" : "text-red-400"}>
          Dhan API: {!dc.connected
            ? "NOT CONNECTED"
            : dc.api_ok ? "OK (token valid)" : `REJECTED — ${dc.error_code}`}
        </span>
        <span className="txt-muted">
          Last refresh: {dc.last_token_refresh ? dc.last_token_refresh.slice(0, 16) : "never"}
        </span>
        {dc.last_dh901_at && (
          <span className="text-amber-400">Expired since: {dc.last_dh901_at.slice(0, 16)}</span>
        )}
        {dc.connected && !dc.api_ok && (
          <button
            onClick={onRefreshToken}
            disabled={refreshing}
            className="ml-auto flex items-center gap-1.5 font-section tracking-wider border border-red-500/50 px-3 py-1 text-red-300 hover:text-white hover:border-white/30 transition-colors disabled:opacity-40"
          >
            {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wifi className="w-3 h-3" />}
            {refreshing ? "REFRESHING…" : "REFRESH DHAN TOKEN"}
          </button>
        )}
      </div>

      {/* MCX IDs */}
      {mcx_missing_ids?.length > 0 && (
        <div className="text-xs text-amber-400 flex items-center gap-2">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          MCX security IDs missing: {mcx_missing_ids.join(", ")} — restart backend to rediscover
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-3 text-center text-xs">
        <div className="bg-[var(--bg-page)] p-2">
          <div className="txt-muted overline text-[10px] mb-0.5">TODAY'S CANDIDATES</div>
          <div className="font-mono-data text-white text-lg">{today_candidates?.total ?? 0}</div>
        </div>
        <div className="bg-[var(--bg-page)] p-2">
          <div className="txt-muted overline text-[10px] mb-0.5">ORB SIGNALS 14D</div>
          <div className={`font-mono-data text-lg ${(signals_last_14d?.total ?? 0) === 0 ? "text-red-400" : "text-emerald-400"}`}>
            {signals_last_14d?.total ?? 0}
          </div>
        </div>
        <div className="bg-[var(--bg-page)] p-2">
          <div className="txt-muted overline text-[10px] mb-0.5">TRIGGERED 14D</div>
          <div className="font-mono-data text-white text-lg">
            {Object.values(status.candidates_last_14d?.by_day ?? {}).reduce((a, b) => a + b, 0)}
          </div>
        </div>
      </div>

      {/* Diagnosis */}
      <div className="space-y-1.5">
        {diagnosis.map((d, i) => {
          const isCrit = d.startsWith("CRITICAL");
          const isWarn = d.startsWith("WARNING");
          return (
            <div key={i} className={`text-xs px-3 py-2 ${
              isCrit ? "bg-red-500/10 text-red-300 border border-red-500/20"
              : isWarn ? "bg-amber-500/10 text-amber-300 border border-amber-500/20"
              : "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20"
            }`}>
              {d}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ORBWatchlist() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [scanning, setScanning]     = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [chainSymbol, setChainSymbol] = useState(null);
  const [status, setStatus]         = useState(null);
  const intervalRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/orb/candidates");
      setCandidates(r.data.candidates || []);
      setLastUpdate(new Date());
    } catch {
      // silently ignore — stale data is fine
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStatus = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const r = await api.get("/orb/status");
      setStatus(r.data);
    } catch { /* ignore */ }
  }, [isAdmin]);

  const [refreshingToken, setRefreshingToken] = useState(false);
  const refreshToken = useCallback(async () => {
    setRefreshingToken(true);
    try {
      const r = await api.post("/orb/refresh-token");
      if (r.data.ok) toast.success("Dhan token refreshed — API OK");
      else toast.error(`Token refresh failed: ${r.data.error_code} ${r.data.error_message || ""}`);
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Token refresh failed");
    } finally {
      setRefreshingToken(false);
    }
  }, [loadStatus]);

  const rescan = useCallback(async () => {
    setScanning(true);
    try {
      const r = await api.post("/orb/scan");
      toast.success(`ORB scan complete — ${r.data.candidates} candidate${r.data.candidates !== 1 ? "s" : ""} found`);
      await load();
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  }, [load, loadStatus]);

  useEffect(() => {
    load();
    loadStatus();
    // Auto-refresh every 30 seconds during market hours
    intervalRef.current = setInterval(load, 30_000);
    return () => clearInterval(intervalRef.current);
  }, [load, loadStatus]);

  const bullish = candidates.filter(c => c.direction === "BULLISH");
  const bearish = candidates.filter(c => c.direction === "BEARISH");

  return (
    <AppShell>
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        <PageHeader
          overline="Live Strategy"
          title="ORB Intraday Watchlist"
          subtitle="Opening Range Breakout — 09:15 candle forms the range, 09:30+ monitors for breakout"
        />

        {/* Info bar */}
        <div className="panel p-4 flex flex-wrap items-center gap-6 text-xs">
          <div className="flex items-center gap-2 txt-muted">
            <Clock className="w-4 h-4" />
            <span>Candidates identified at <strong className="text-white">09:30 IST</strong></span>
          </div>
          <div className="flex items-center gap-2 txt-muted">
            <Zap className="w-4 h-4 text-amber-400" />
            <span>Breakout monitored every <strong className="text-white">1 min</strong> via Dhan API</span>
          </div>
          <div className="flex items-center gap-2 txt-muted">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <span>Force exit at <strong className="text-white">15:15 IST</strong></span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {lastUpdate && (
              <span className="txt-muted">Updated {lastUpdate.toLocaleTimeString("en-IN")}</span>
            )}
            <button
              onClick={load}
              disabled={loading}
              className="flex items-center gap-1.5 text-xs font-section tracking-wider border border-[var(--border)] px-3 py-1.5 txt-muted hover:text-white transition-colors disabled:opacity-40"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> REFRESH
            </button>
            <button
              onClick={rescan}
              disabled={scanning}
              className="flex items-center gap-1.5 text-xs font-section tracking-wider border border-amber-500/50 px-3 py-1.5 text-amber-400 hover:text-white hover:border-white/30 transition-colors disabled:opacity-40"
            >
              {scanning
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Zap className="w-3 h-3" />}
              {scanning ? "SCANNING…" : "RESCAN"}
            </button>
          </div>
        </div>

        {/* Admin diagnostic panel */}
        {isAdmin && status && (
          <DiagnosticPanel status={status} onRefreshToken={refreshToken} refreshing={refreshingToken} />
        )}

        {/* Setup legend */}
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div className="panel p-3 border-l-2 border-emerald-500">
            <div className="font-section text-emerald-400 mb-1">BULLISH SETUP</div>
            <div className="txt-muted">Open ≈ Low at 09:15 candle → price gapped up, held the open as support → buy CE on break of OR High</div>
          </div>
          <div className="panel p-3 border-l-2 border-red-500">
            <div className="font-section text-red-400 mb-1">BEARISH SETUP</div>
            <div className="txt-muted">Open ≈ High at 09:15 candle → price gapped down, held the open as resistance → buy PE on break of OR Low</div>
          </div>
        </div>

        {loading && (
          <div className="panel p-12 text-center txt-muted text-sm">
            Scanning for ORB candidates…
          </div>
        )}

        {!loading && candidates.length === 0 && (
          <div className="panel p-12 text-center space-y-2">
            <div className="txt-muted text-sm">No ORB candidates found for today.</div>
            <div className="text-xs txt-muted">
              Candidates are identified at 09:30 IST when the opening range is complete.
              Requires Dhan broker connection for live intraday data.
            </div>
          </div>
        )}

        {!loading && candidates.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bullish */}
            {bullish.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                  <span className="font-section text-emerald-400 text-sm tracking-wider">
                    BULLISH SETUPS ({bullish.length})
                  </span>
                </div>
                {bullish.map(c => (
                  <CandidateRow key={c.symbol} c={c} onChain={setChainSymbol} />
                ))}
              </div>
            )}

            {/* Bearish */}
            {bearish.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-red-400" />
                  <span className="font-section text-red-400 text-sm tracking-wider">
                    BEARISH SETUPS ({bearish.length})
                  </span>
                </div>
                {bearish.map(c => (
                  <CandidateRow key={c.symbol} c={c} onChain={setChainSymbol} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Strategy summary */}
        <div className="panel p-4 text-xs txt-muted space-y-1">
          <div className="font-section text-white mb-2">EXECUTION RULES</div>
          <div>• Entry: 1-min candle <strong className="text-white">closes above OR High</strong> (CE) or <strong className="text-white">closes below OR Low</strong> (PE)</div>
          <div>• SL: OR Low for CE | OR High for PE (underlying price)</div>
          <div>• Target: Entry ± <strong className="text-white">2R</strong> (2× the risk distance)</div>
          <div>• Force Exit: <strong className="text-white">15:15 IST</strong> regardless of P&L</div>
          <div>• Signals auto-appear in Signal Feed when breakout triggers</div>
        </div>
      </div>

      {chainSymbol && (
        <ChainModal symbol={chainSymbol} onClose={() => setChainSymbol(null)} />
      )}
    </AppShell>
  );
}
